import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Typography, Form, Row, Col, Input, Space, Button, Divider, message, Drawer } from "antd";
import { ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import BaseSelect from "@/components/Elements/BaseSelect";
import BaseRadioList from "@/components/Elements/BaseRadios";
import { createOrthodonticExam, getOrthodonticExamByCustomer, getSpecificOrthodonticExam, updateOrthodonticExam } from "@/redux/actions/orthodonticExamActions";
import CheckDetailForm from "@/components/CheckDetailForm";
import { isMobile } from "react-device-detect";
import { getSpecificCustomer } from "@/redux/actions/customerActions";

const { Title } = Typography;

const OrthodonticExamForm = (props) => {

    const { visible, onCancel, customer, record } = props;

    const customerId = customer?.id;

    const dispatch = useDispatch();
    const [form] = Form.useForm();

    const [isLoading, setIsLoading] = useState(false);
    const [isApproved, setIsApproved] = useState(false);
    const [isCreateMode, setIsCreateMode] = useState(false);

    const doctors = useSelector((state) => state.config.doctors) || [];
    const assistants = useSelector((state) => state.config.assistants) || [];
    const currentOrthodonticExam = useSelector(s => s.orthodonticExamReducer.currentOrthodonticExam);

    const onFinish = useCallback(
        async (values) => {
            if (!customer?.id) return;
            //console.log("values", values);
            setIsLoading(true);
            try {
                const formatted = {
                    ...values,
                };
                console.log("formatted", formatted);
                if (record && record.id) {
                    await dispatch(updateOrthodonticExam(customerId, record.id, formatted));
                    await dispatch(getSpecificOrthodonticExam(record.id));
                } else {
                    await dispatch(createOrthodonticExam(customerId, formatted));
                    await dispatch(getOrthodonticExamByCustomer({}, customerId));

                    // Đóng form
                    handleClose();
                }
                await dispatch(getSpecificCustomer(customerId));
            } finally {
                setIsLoading(false);
            }
        },
        [dispatch, customer?.id, record?.id]
    );

    const onFinishFailed = (err) => {
        message.error('Vui lòng điền đầy đủ các trường bắt buộc');
    };

    const defaultValues = {
        bac_si_phu_trach: null,
        y_ta_phu_trach_ids: [],
    };

    const huong_dieu_tri_so_bo_placeholder = [
        "1. Định hướng điều trị: Ca nhổ răng hay không nhổ răng tiền cối",
        "2. Chỉ định nhổ răng: Ghi rõ răng cần nhổ và thời điểm chỉ định nhổ (trước/ trong hoặc sau chỉnh nha) (cả răng tiền cối, răng khôn và các răng khác nếu có)",
        "3. Gói chỉnh nha: Ví dụ Gói mắc cài/ INV loại… / số…_ Level ca?",
        "4. Khí cụ hỗ trợ: Ví dụ Dự trù 2-4 mini vis/ Khí cụ nong hàm ….",
        "5. Thủ thuật hỗ trợ và yêu cầu hợp tác: Ví dụ: mài kẽ/ mang thun liên hàm",
        "6. Thời gian điều trị dự kiến: Ví dụ 24-30 tháng",
    ].join("\n");

    const handleClose = () => {
        setIsCreateMode(false);
        form.resetFields();
        setIsApproved(false);
        onCancel();
    };

    useEffect(() => {
        if (visible) {
            setIsCreateMode(!record?.id);
        }
    }, [visible, record]);

    useEffect(() => {
        if (!visible) return;

        // CREATE MODE → reset clean form
        if (isCreateMode) {
            form.resetFields();
            form.setFieldsValue(defaultValues);
            setIsApproved(false);
            return;
        }

        // UPDATE MODE → fill from currentExam or record
        const source = currentOrthodonticExam?.id ? currentOrthodonticExam : record;

        if (source && source.id) {
            const merged = {
                ...defaultValues,
                ...source
            };

            form.resetFields();
            form.setFieldsValue(merged);
            setIsApproved(source.is_approved || false);
            return;
        }

    }, [visible, isCreateMode, currentOrthodonticExam, record, defaultValues]);

    return (
        <div>
            <Drawer
                visible={visible}
                title={record ? "Cập nhật phiếu khám chỉnh nha" : "Thêm phiếu khám chỉnh nha"}
                onClose={handleClose}
                closable
                maskClosable={false}
                width={isMobile ? '100%' : '80%'}
                forceRender
                footer={
                    <div style={{ textAlign: "right" }}>
                        <Space>
                            <Button
                                htmlType="button"
                                size="large"
                                onClick={() => form.resetFields()}
                                icon={<ReloadOutlined />}
                            >
                                Reset
                            </Button>
                            <Button icon={<SaveOutlined />} size='large' loading={isLoading} type="primary" onClick={() => form.submit()}>
                                Lưu
                            </Button>
                        </Space>
                    </div>
                }
            >
                {visible && (
                    <Form
                        form={form}
                        layout="horizontal"
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        disabled={isApproved}
                        initialValues={undefined}
                        className="form-readonly-custom-bg horizontal-custom-item"
                    >
                        <Row gutter={8}>
                            <Col xs={24} md={24}>
                                <Form.Item
                                    label="Bác sĩ phụ trách"
                                    name="bac_si_phu_trach"
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <BaseSelect
                                        options={doctors || []}
                                        optionValue="user_id"
                                        optionLabel="display_name"
                                        showSearch
                                        placeholder="Chọn bác sĩ"
                                        className="w-100"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item
                                    label="Y tá phụ trách"
                                    name="y_ta_phu_trach_ids"
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <BaseSelect
                                        options={assistants || []}
                                        optionValue="user_id"
                                        optionLabel="display_name"
                                        showSearch
                                        mode="multiple"
                                        placeholder="Chọn một hoặc nhiều y tá"
                                        className="w-100"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Divider orientation="left" orientationMargin="0" className='mt-3 mb-0'>I. BĂN KHOĂN CỦA BỆNH NHÂN </Divider>
                        <Row gutter={8}>
                            <Col xs={24} md={24} className="mt-2 mb-2">
                                <Form.Item name="ban_khoan_cua_benh_nhan">
                                    <Input.TextArea rows={4} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Divider orientation="left" orientationMargin="0" className='mt-3 mb-0'>II. KHÁM TRONG MIỆNG </Divider>
                        <Title level={5} strong className="mb-3 mt-3">1. Đánh giá tình trạng răng và cung răng</Title>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item
                                    label="Kích thước cung răng"
                                    name="kich_thuoc_cung_rang"
                                >
                                    <BaseRadioList
                                        options={[
                                            { value: "Hẹp", label: "Hẹp" },
                                            { value: "Rộng", label: "Rộng" },
                                            { value: "Trung bình", label: "Trung bình (hẹp/rộng/trung bình)" }
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    label="Hàm trên: Mức độ chen chúc ước tính"
                                    name="ham_tren_muc_do_chen_chuc"
                                >
                                    <BaseRadioList
                                        options={[
                                            { value: ">7mm", label: "> 7mm" },
                                            { value: "4-6mm", label: "4-6mm" },
                                            { value: "<3mm", label: "<3mm" }
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    label="Hàm dưới: Mức độ chen chúc ước tính"
                                    name="ham_duoi_muc_do_chen_chuc"
                                >
                                    <BaseRadioList
                                        options={[
                                            { value: ">7mm", label: "> 7mm" },
                                            { value: "4-6mm", label: "4-6mm" },
                                            { value: "<3mm", label: "<3mm" }
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={8}>
                            <Col span={24}>
                                <CheckDetailForm
                                    style={{ marginBottom: "10px" }}
                                    listName="khe_thua_ham_tren"
                                    data={[
                                        {
                                            label: "Khe thưa hàm trên",
                                            inputType: "rank",
                                            options: [
                                                { value: "Không", label: "Không" },
                                                { value: "Có", label: "Có (ghi rõ chi tiết)" }
                                            ],
                                            triggerValues: ["Có"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                        },
                                    ]}
                                    columns={[
                                        {
                                            title: "",
                                            dataIndex: "label",
                                            width: "150px",
                                            render: (_, record, index) => (
                                                <>
                                                    <span style={{ fontWeight: 500 }} className="check-detail-label">{record.label}</span>
                                                    <Form.Item name={[index, "label"]} initialValue={record.label} hidden>
                                                        <Input />
                                                    </Form.Item>
                                                </>
                                            ),
                                        },
                                    ]}
                                    requiredColWidth="300px"
                                />
                            </Col>
                            <Col span={24}>
                                <CheckDetailForm
                                    style={{ marginBottom: "10px" }}
                                    listName="khe_thua_ham_duoi"
                                    data={[
                                        {
                                            label: "Khe thưa hàm dưới",
                                            inputType: "rank",
                                            options: [
                                                { value: "Không", label: "Không" },
                                                { value: "Có", label: "Có (ghi rõ chi tiết)" }
                                            ],
                                            triggerValues: ["Có"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                        },
                                    ]}
                                    columns={[
                                        {
                                            title: "",
                                            dataIndex: "label",
                                            width: "150px",
                                            render: (_, record, index) => (
                                                <>
                                                    <span style={{ fontWeight: 500 }} className="check-detail-label">{record.label}</span>
                                                    <Form.Item name={[index, "label"]} initialValue={record.label} hidden>
                                                        <Input />
                                                    </Form.Item>
                                                </>
                                            ),
                                        },
                                    ]}
                                    requiredColWidth="300px"
                                />
                            </Col>
                            <Col span={24}>
                                <CheckDetailForm
                                    style={{ marginBottom: "10px" }}
                                    listName="thua_thieu_rang"
                                    data={[
                                        {
                                            label: "Bệnh nhân có thiếu/thừa răng không?",
                                            inputType: "rank",
                                            options: [
                                                { value: "Không", label: "Không" },
                                                { value: "Có", label: "Có (ghi rõ chi tiết)" }
                                            ],
                                            triggerValues: ["Có"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                        },
                                    ]}
                                    columns={[
                                        {
                                            title: "",
                                            dataIndex: "label",
                                            width: "300px",
                                            render: (_, record, index) => (
                                                <>
                                                    <span style={{ fontWeight: 500 }} className="check-detail-label">{record.label}</span>
                                                    <Form.Item name={[index, "label"]} initialValue={record.label} hidden>
                                                        <Input />
                                                    </Form.Item>
                                                </>
                                            ),
                                        },
                                    ]}
                                    requiredColWidth="300px"
                                />
                            </Col>
                        </Row>
                        <Title level={5} style={{ fontSize: 15 }} strong className="mb-2 mt-1">
                            Bệnh nhân có bất thường gì về kích thước / hình dáng / độ xoay lệch răng hoặc bất thường khác cần lưu ý khi chỉnh nha không? (Bác sĩ liệt kê)
                        </Title>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item name="bat_thuong_rang_can_luu_y">
                                    <Input.TextArea rows={3} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Title level={5} strong className="mb-3 mt-3">2. Đánh giá tình trạng khớp cắn</Title>
                        <Row gutter={8}>
                            <Col span={8}>
                                <Form.Item
                                    label="Độ trùm"
                                    name="do_trum"
                                >
                                    <Input suffix="mm" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={8}>
                            <Col span={8}>
                                <Form.Item
                                    label="Độ chìa"
                                    name="do_chia"
                                >
                                    <Input suffix="mm" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Title level={5} strong className="mb-3 mt-3">Khớp cắn bên trái</Title>
                        <Row gutter={8}>
                            <Col span={24}>
                                <CheckDetailForm
                                    style={{ marginBottom: '10px' }}
                                    listName="rang_3_trai"
                                    data={[
                                        {
                                            label: "Răng 3",
                                            inputType: "rank",
                                            options: [
                                                { value: "Hạng 1", label: "Hạng 1" },
                                                { value: "Hạng 2", label: "Hạng 2" },
                                                { value: "Hạng 3", label: "Hạng 3" },
                                            ],
                                            triggerValues: ["Hạng 2", "Hạng 3"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                        },
                                    ]}
                                    columns={[
                                        {
                                            title: "",
                                            dataIndex: "label",
                                            width: "100px",
                                            render: (_, record, index) => (
                                                <>
                                                    <span style={{ fontWeight: 500 }} className="check-detail-label">{record.label}</span>
                                                    <Form.Item name={[index, "label"]} initialValue={record.label} hidden>
                                                        <Input />
                                                    </Form.Item>
                                                </>
                                            ),
                                        },
                                    ]}
                                    requiredColWidth="300px"
                                    placeholder="mm"
                                />
                            </Col>
                        </Row>
                        <Row gutter={8}>
                            <Col span={24}>
                                <CheckDetailForm
                                    listName="rang_6_trai"
                                    data={[
                                        {
                                            label: "Răng 6",
                                            inputType: "rank",
                                            options: [
                                                { value: "Hạng 1", label: "Hạng 1" },
                                                { value: "Hạng 2", label: "Hạng 2" },
                                                { value: "Hạng 3", label: "Hạng 3" },
                                            ],
                                            triggerValues: ["Hạng 2", "Hạng 3"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                        },
                                    ]}
                                    columns={[
                                        {
                                            title: "",
                                            dataIndex: "label",
                                            width: "100px",
                                            render: (_, record, index) => (
                                                <>
                                                    <span style={{ fontWeight: 500 }} className="check-detail-label">{record.label}</span>
                                                    <Form.Item name={[index, "label"]} initialValue={record.label} hidden>
                                                        <Input />
                                                    </Form.Item>
                                                </>
                                            ),
                                        },
                                    ]}
                                    requiredColWidth="300px"
                                    placeholder="mm"
                                />
                            </Col>
                        </Row>
                        <Title level={5} strong className="mb-3 mt-3">Khớp cắn bên phải</Title>
                        <Row gutter={8}>
                            <Col span={24}>
                                <CheckDetailForm
                                    style={{ marginBottom: '10px' }}
                                    listName="rang_3_phai"
                                    data={[
                                        {
                                            label: "Răng 3",
                                            inputType: "rank",
                                            options: [
                                                { value: "Hạng 1", label: "Hạng 1" },
                                                { value: "Hạng 2", label: "Hạng 2" },
                                                { value: "Hạng 3", label: "Hạng 3" },
                                            ],
                                            triggerValues: ["Hạng 2", "Hạng 3"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                        },
                                    ]}
                                    columns={[
                                        {
                                            title: "",
                                            dataIndex: "label",
                                            width: "100px",
                                            render: (_, record, index) => (
                                                <>
                                                    <span style={{ fontWeight: 500 }} className="check-detail-label">{record.label}</span>
                                                    <Form.Item name={[index, "label"]} initialValue={record.label} hidden>
                                                        <Input />
                                                    </Form.Item>
                                                </>
                                            ),
                                        },
                                    ]}
                                    requiredColWidth="300px"
                                    placeholder="mm"
                                />
                            </Col>
                        </Row>
                        <Row gutter={8}>
                            <Col span={24}>
                                <CheckDetailForm
                                    style={{ marginBottom: '10px' }}
                                    listName="rang_6_phai"
                                    data={[
                                        {
                                            label: "Răng 6",
                                            inputType: "rank",
                                            options: [
                                                { value: "Hạng 1", label: "Hạng 1" },
                                                { value: "Hạng 2", label: "Hạng 2" },
                                                { value: "Hạng 3", label: "Hạng 3" },
                                            ],
                                            triggerValues: ["Hạng 2", "Hạng 3"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                        },
                                    ]}
                                    columns={[
                                        {
                                            title: "",
                                            dataIndex: "label",
                                            width: "100px",
                                            render: (_, record, index) => (
                                                <>
                                                    <span style={{ fontWeight: 500 }} className="check-detail-label">{record.label}</span>
                                                    <Form.Item name={[index, "label"]} initialValue={record.label} hidden>
                                                        <Input />
                                                    </Form.Item>
                                                </>
                                            ),
                                        },
                                    ]}
                                    requiredColWidth="300px"
                                    placeholder="mm"
                                />
                            </Col>
                        </Row>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item
                                    label="Đường cong spee"
                                    name="duong_cong_spee"
                                >
                                    <BaseRadioList
                                        options={[
                                            { value: "Bình thường", label: "Bình thường" },
                                            { value: "Sâu", label: "Sâu" }
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={8}>
                            <Col span={24}>
                                <CheckDetailForm
                                    style={{ marginBottom: "10px" }}
                                    listName="mat_phang_can"
                                    data={[
                                        {
                                            label: "Mặt phẳng cắn",
                                            inputType: "rank",
                                            options: [
                                                { value: "Bình thường", label: "Bình thường" },
                                                { value: "Nghiêng", label: "Nghiêng (ghi rõ chi tiết)" }
                                            ],
                                            triggerValues: ["Nghiêng"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                        },
                                    ]}
                                    columns={[
                                        {
                                            title: "",
                                            dataIndex: "label",
                                            width: "150px",
                                            render: (_, record, index) => (
                                                <>
                                                    <span style={{ fontWeight: 500 }} className="check-detail-label">{record.label}</span>
                                                    <Form.Item name={[index, "label"]} initialValue={record.label} hidden>
                                                        <Input />
                                                    </Form.Item>
                                                </>
                                            ),
                                        },
                                    ]}
                                    requiredColWidth="400px"
                                />
                            </Col>
                        </Row>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item
                                    label="Loại mô nha chu"
                                    name="loai_mo_nha_chu"
                                >
                                    <BaseRadioList
                                        options={[
                                            { value: "Bình thường", label: "Bình thường" },
                                            { value: "Dày", label: "Dày" },
                                            { value: "Mỏng", label: "Mỏng" }
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    label="Cắn lệch chức năng"
                                    name="can_lech_chuc_nang"
                                >
                                    <BaseRadioList
                                        options={[
                                            { value: "Có", label: "Có" },
                                            { value: "Không", label: "Không" }
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Title level={5} style={{ fontSize: 15 }} strong className="mb-2 mt-1">
                            Bệnh nhân có bất thường gì về khớp cắn khác cần lưu ý khi chỉnh nha không? (Bác sĩ liệt kê)
                        </Title>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item name="khop_can_bat_thuong_can_luu_y">
                                    <Input.TextArea rows={3} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Title level={5} strong className="mb-3 mt-3">3. Các ghi chú và quan sát khác</Title>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item
                                    label="Kích thước lưỡi"
                                    name="kich_thuoc_luoi"
                                >
                                    <BaseRadioList
                                        options={[
                                            { value: "Bình thường", label: "Bình thường" },
                                            { value: "To", label: "To" }
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    label="Tật đẩy lưỡi"
                                    name="tat_day_luoi"
                                >
                                    <BaseRadioList
                                        options={[
                                            { value: "Có", label: "Có" },
                                            { value: "Không", label: "Không" }
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Title level={5} style={{ fontSize: 15 }} strong className="mb-2 mt-1">
                            Quan sát hoặc lưu ý khác của bác sĩ về bệnh nhân mà cần được cân nhắc trong quá trình chỉnh nha? (Bác sĩ liệt kê)
                        </Title>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item name="quan_sat_va_luu_y_khac_chinh_nha">
                                    <Input.TextArea rows={3} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider orientation="left" orientationMargin="0" className='mt-3 mb-0'>III. KHÁM NGOÀI MẶT</Divider>
                        <Title level={5} strong className="mb-3 mt-3">
                            1. Đánh giá mặt thẳng ở tư thế nghỉ
                        </Title>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item label="Dạng mặt thẳng" name="dang_mat_thang">
                                    <BaseRadioList
                                        options={[
                                            { value: "Bình thường", label: "Bình thường" },
                                            { value: "Mặt ngắn", label: "Mặt ngắn" },
                                            { value: "Mặt dài", label: "Mặt dài" },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item label="Độ cân đối của cằm" name="do_can_doi_cam">
                                    <BaseRadioList
                                        options={[
                                            { value: "Lệch", label: "Lệch" },
                                            { value: "Không lệch", label: "Không lệch" },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    label="Tương quan tầng mặt dưới so với tổng thể khuôn mặt"
                                    name="tuong_quan_tang_mat_duoi"
                                >
                                    <BaseRadioList
                                        options={[
                                            { value: "Hài hòa", label: "Hài hòa" },
                                            { value: "Ngắn", label: "Ngắn" },
                                            { value: "Dài", label: "Dài" },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item label="Môi có khép kín ở tư thế nghỉ hay không" name="moi_khep_nghi">
                                    <BaseRadioList
                                        options={[
                                            { value: "Có", label: "Có" },
                                            { value: "Không", label: "Không" },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item label="Rãnh môi cằm" name="ranh_moi_cam">
                                    <BaseRadioList
                                        options={[
                                            { value: "Bình thường", label: "Bình thường" },
                                            { value: "Thấy rõ", label: "Thấy rõ" },
                                            { value: "Không thấy", label: "Không thấy" },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Title level={5} style={{ fontSize: 15 }} strong className="mb-2 mt-1">
                            Ngoài ra có bất thường gì nổi trội khác hay không?
                        </Title>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item name="bat_thuong_mat_thang_nghi" >
                                    <Input.TextArea rows={2} placeholder="Ghi chú nếu có bất thường khác..." />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Title level={5} strong className="mb-3 mt-3">
                            2. Đánh giá mặt thẳng ở tư thế cười
                        </Title>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item label="Đường cười" name="duong_cuoi">
                                    <BaseRadioList
                                        options={[
                                            { value: "Bình thường", label: "Bình thường" },
                                            { value: "Cao", label: "Cao" },
                                            { value: "Thấp", label: "Thấp" },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <CheckDetailForm
                                    style={{ marginBottom: "10px" }}
                                    listName="cuoi_lo_nuou"
                                    data={[
                                        {
                                            label: "Cười lộ nướu",
                                            inputType: "rank",
                                            options: [
                                                { value: "Không", label: "Không" },
                                                { value: "Có", label: "Có" }
                                            ],
                                            triggerValues: ["Có"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                        },
                                    ]}
                                    columns={[
                                        {
                                            title: "",
                                            dataIndex: "label",
                                            width: "100px",
                                            render: (_, record, index) => (
                                                <>
                                                    <span style={{ fontWeight: 500 }} className="check-detail-label">{record.label}</span>
                                                    <Form.Item name={[index, "label"]} initialValue={record.label} hidden>
                                                        <Input />
                                                    </Form.Item>
                                                </>
                                            ),
                                        },
                                    ]}
                                    requiredColWidth="300px"
                                    placeholder="mm"
                                />
                            </Col>
                        </Row>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item label="Hành lang má" name="hanh_lang_ma">
                                    <BaseRadioList
                                        options={[
                                            { value: "Bình thường", label: "Bình thường" },
                                            { value: "Tối", label: "Tối" }
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <CheckDetailForm
                                    style={{ marginBottom: "10px" }}
                                    listName="duong_giua"
                                    data={[
                                        {
                                            label: "Đường giữa",
                                            inputType: "rank",
                                            options: [
                                                { value: "Không lệch", label: "Không lệch" },
                                                { value: "Lệch", label: "Lệch" }
                                            ],
                                            triggerValues: ["Lệch"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                        },
                                    ]}
                                    columns={[
                                        {
                                            title: "",
                                            dataIndex: "label",
                                            width: "100px",
                                            render: (_, record, index) => (
                                                <>
                                                    <span style={{ fontWeight: 500 }} className="check-detail-label">{record.label}</span>
                                                    <Form.Item name={[index, "label"]} initialValue={record.label} hidden>
                                                        <Input />
                                                    </Form.Item>
                                                </>
                                            ),
                                        },
                                    ]}
                                    requiredColWidth="300px"
                                    placeholder="mm"
                                />
                            </Col>
                        </Row>

                        <Title level={5} style={{ fontSize: 15 }} strong className="mb-2 mt-1">
                            Ngoài ra có bất thường gì nổi trội khác hay không?
                        </Title>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item name="bat_thuong_mat_thang_cuoi">
                                    <Input.TextArea rows={2} placeholder="Ghi chú thêm nếu có..." />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Title level={5} strong className="mb-3 mt-3">
                            3. Đánh giá mặt nghiêng ở tư thế nghỉ
                        </Title>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item label="Dạng mặt nghiêng" name="dang_mat_nghieng">
                                    <BaseRadioList
                                        options={[
                                            { value: "Lồi", label: "Lồi" },
                                            { value: "Lõm", label: "Lõm" },
                                            { value: "Thẳng", label: "Thẳng" },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item label="Góc môi trên" name="goc_moi_tren">
                                    <BaseRadioList
                                        options={[
                                            { value: "Bình thường", label: "Bình thường" },
                                            { value: "Tù", label: "Tù" },
                                            { value: "Nhọn", label: "Nhọn" },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item label="Vị trí cằm" name="vi_tri_cam">
                                    <BaseRadioList
                                        options={[
                                            { value: "Bình thường", label: "Bình thường" },
                                            { value: "Ra trước", label: "Ra trước" },
                                            { value: "Lùi sau", label: "Lùi sau" },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item label="Góc mặt phẳng hàm dưới" name="goc_mat_phang_ham_duoi">
                                    <BaseRadioList
                                        options={[
                                            { value: "Bình thường", label: "Bình thường" },
                                            { value: "Đóng", label: "Đóng" },
                                            { value: "Mở", label: "Mở" },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item label="Lõm môi cằm" name="lom_moi_cam">
                                    <BaseRadioList
                                        options={[
                                            { value: "Bình thường", label: "Bình thường" },
                                            { value: "Thấy rõ", label: "Thấy rõ" },
                                            { value: "Không thấy", label: "Không thấy" },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item label="Hình thái môi" name="hinh_thai_moi">
                                    <BaseRadioList
                                        options={[
                                            { value: "Bình thường", label: "Bình thường" },
                                            { value: "Dày", label: "Dày" },
                                            { value: "Mỏng", label: "Mỏng" },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider orientation="left" orientationMargin="0" className='mt-3 mb-0'>IV. HƯỚNG ĐIỀU TRỊ SƠ BỘ </Divider>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item name="huong_dieu_tri_so_bo">
                                    <Input.TextArea
                                        rows={15}
                                        placeholder={huong_dieu_tri_so_bo_placeholder}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                )}
            </Drawer>
        </div >
    );
};

export default OrthodonticExamForm;
